// @flow
import React from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import _ from "lodash";
import LazyLoad from "react-lazy-load";
import Heart from "./icons/Heart";
import Video from "./icons/Video";
import Tag from "./Tag";
import ListingCallouts from "./ListingCallouts";
import ListingCardPhoto from "./ListingCardPhoto";
import type { CompliantListingType } from "flow/types";
import { withGlobalContext } from "./GlobalContext";

type Props = {
  classes: Object,
  className: string,
  compliance: Object,
  favorites: Array<number>,
  openHouses: Array<Object>,
  listing: CompliantListingType,
  onPdpNavigation: Function,
  pdpAction: string,
  pdpList: Array<Object>,
  toggleFavoriteListing: Function,
  trackLeadAction: Function,
  title: string,
  eagerLoad?: boolean,
};

const ListingCard = ({
  classes,
  className,
  compliance,
  favorites,
  openHouses,
  listing,
  onPdpNavigation,
  pdpAction,
  pdpList,
  toggleFavoriteListing,
  trackLeadAction,
  eagerLoad,
}: Props) => {
  const isFavorite = favorites && favorites.indexOf(listing.id) > -1;
  const hasOpenHouse =
    openHouses &&
    openHouses.length &&
    _.map(openHouses, (openHouse) => {
      return openHouse.listingId;
    }).indexOf(listing.id) > -1;
  let listingOpenHouses = null;
  if (hasOpenHouse) {
    listingOpenHouses = _.filter(openHouses, (openHouse) => {
      return openHouse.listingId === listing.id;
    });
  }

  const watermarkQuery =
    compliance && compliance.photoWatermark
      ? `&mark=${compliance.photoWatermark}&markscale=${compliance.markScale}`
      : "";

  return (
    <a
      href={`/listings/${listing.detailsUrlSlug}`}
      className={classNames(classes.root, className)}
      onClick={(e) => {
        e.preventDefault();
        onPdpNavigation({
          action: pdpAction || "none",
          currentUrl: window.location.href,
          pdpList: pdpList || [],
          url: `/listings/${listing.detailsUrlSlug}`,
        });
      }}
    >
      <div className={classes.photo}>
        <span className={classNames(classes.tag, classes.onTop)}>
          {listing.isNew ? (
            <Tag status="isNew" title="Just Listed" />
          ) : listing.isBackOnMarket ? (
            <Tag status="isBackOnMarket" title="Back On Market" />
          ) : listing.isPriceReduced ? (
            <Tag
              status="isPriceReduced"
              title={`Price Reduced ${listing.priceReductionAmount}`}
            />
          ) : hasOpenHouse ? (
            <Tag
              status="hasOpenHouse"
              title={_.first(listingOpenHouses).openHouseDescription}
            />
          ) : null}
        </span>
        <button
          className={classNames(
            classes.iconButton,
            classes.heart,
            classes.onTop,
            isFavorite && classes.heartActive
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation(); // Block parent `a href` from firing
            toggleFavoriteListing(listing.id, listing.mlsListingId);
          }}
          title="Save to favorites"
        >
          <Heart />
        </button>
        {listing.virtualTour && (
          <button
            className={classNames(
              classes.iconButton,
              classes.video,
              classes.onTop
            )}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // Block parent `a href` from firing
              trackLeadAction({
                action: "viewed-virtual-tour",
                listingId: `${listing.id}`, // API expects listingId as a string
                virtualTourUrl: listing.virtualTour,
              });
              window.open(listing.virtualTour, "_blank"); // Need to do this as a `button` because we shouldn't nest `a tags`
            }}
            title="View a virtual tour"
          >
            <Video />
          </button>
        )}
        <div className={classes.imgContainer}>
          {eagerLoad ? (
            <ListingCardPhoto
              classes={classes}
              photo={listing.firstPhoto}
              query={watermarkQuery}
              address={listing.fullAddress}
            />
          ) : (
            <LazyLoad offset={1500}>
              <ListingCardPhoto
                classes={classes}
                photo={listing.firstPhoto}
                query={watermarkQuery}
                address={listing.fullAddress}
              />
            </LazyLoad>
          )}
        </div>
      </div>
      <div className={classes.details}>
        <div className={classes.topDetails}>
          {listing.status === "Sold" && listing.closePriceFormatted ? (
            <div className={classes.price}>{listing.closePriceFormatted}</div>
          ) : (
            <div
              className={listing.useRangeClass ? classes.range : classes.price}
            >
              {listing.priceFormatted}
            </div>
          )}

          <span className={classes.type}>{listing.type}</span>
        </div>

        <div className={classes.topDetails}>
          <div className={classes.address}>{listing.address}</div>
          {listing.status === "Active" && (
            <span className={classNames(classes.status, "ref__status-display")}>
              {listing.isRental ? "For Rent" : "For Sale"}
            </span>
          )}
          {listing.status === "Sold" && (
            <span className={classNames(classes.status, "ref__status-display")}>
              {listing.isRental ? "Rented" : "Sold"}
            </span>
          )}
          {listing.status === "Pending" && (
            <span className={classNames(classes.status, "ref__status-display")}>
              Pending
            </span>
          )}
        </div>

        <div className={classes.address}>{listing.cityStateZip}</div>
        {compliance &&
          compliance.listingResults && (
            <div className={classes.compliance}>
              {compliance.listingResults.showMlsId && (
                <div className={classNames(classes.moreDetails, "ref__mlsId")}>
                  MLS #: {listing.mlsListingId}
                </div>
              )}
              {compliance.listingResults.showStatus && (
                <div
                  className={classNames(
                    classes.moreDetails,
                    "ref__listingStatus"
                  )}
                >
                  Status: {listing.status}
                </div>
              )}
              {compliance.listingResults.showBrokerageName &&
                listing.mlsOfficeName && (
                  <div
                    className={classNames(
                      classes.moreDetails,
                      "ref__brokerage"
                    )}
                  >
                    Brokerage: {listing.mlsOfficeName}
                  </div>
                )}
              {compliance.listingResults.showCooperatingBrokerage &&
                listing.status === "Sold" &&
                listing.soldCooperativeOfficeName && (
                  <div
                    className={classNames(
                      classes.moreDetails,
                      "ref_cooperativeBrokerage"
                    )}
                  >
                    Cooperating Brokerage: {listing.soldCooperativeOfficeName}
                  </div>
                )}
              {compliance.listingResults.showMls &&
                listing.mls && (
                  <div
                    className={classNames(
                      classes.moreDetails,
                      "ref__mlsDisclaimer"
                    )}
                  >
                    MLS: {listing.mls}
                  </div>
                )}
              {compliance.listingResults.showBrokerAgentCourtesy &&
                listing.mlsAgentFullName && (
                  <div
                    className={classNames(
                      classes.moreDetails,
                      "ref__brokerAgentCourtesy"
                    )}
                  >
                    Courtesy of {listing.mlsAgentFullName},{" "}
                    {listing.mlsOfficeName}
                  </div>
                )}
              {compliance.listingResults.custom && (
                <div
                  className={classNames(
                    classes.moreDetails,
                    "ref__customDisclaimer"
                  )}
                  dangerouslySetInnerHTML={{
                    __html: compliance.listingResults.custom,
                  }}
                />
              )}
            </div>
          )}
      </div>
      <ListingCallouts isListingCard listing={listing} />
    </a>
  );
};

export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    color: "inherit !important", // remove default `a` tag styling, along with :hover/:active/:etc.
    textDecoration: "inherit !important", // remove default `a` tag styling, along with :hover/:active/:etc.
    background: "#fff",
    borderRadius: (props) => !props.withMap && theme.globalBorderRadius,
    boxShadow: (props) =>
      !props.withMap &&
      "0 2px 4px -1px rgba(0,0,0,.08), 0 2px 2px 0 rgba(0,0,0,.04), 0 2px 4px 0 rgba(0,0,0,.02)", // material-design 1dp
    transition: (props) => !props.withMap && "all 200ms ease-in-out",
    overflow: "hidden",
    userSelect: "none",
    position: (props) => props.withMap && "relative",

    "&:hover": {
      boxShadow: (props) =>
        !props.withMap &&
        "0 5px 5px -3px rgba(0,0,0,.12), 0 8px 10px 1px rgba(0,0,0,.08), 0 3px 14px 2px rgba(0,0,0,.04)", // material-design 8dp
    },
  },
  onTop: {
    zIndex: theme.zIndex.buttonChildren,
  },
  photo: {
    width: "100%",
    position: "relative",

    ".ie-11 &": {
      height: "160px",
    },
  },
  img: {
    maxWidth: "100%",
    width: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    ".ie-11 &": {
      width: "auto",
    },
  },
  imgContainer: {
    position: "relative",
    paddingTop: "57.14%", // if photo sizes are adjusted, ratio needs to be adjusted here
  },
  tag: {
    position: "absolute",
    top: "8px",
    left: "8px",
  },
  iconButton: {
    // we use a button reset here so that when a card is used without a global reset the buttons apppear correctly
    "-webkit-appearance": "none",
    "-moz-appearance": "none",
    userSelect: "none",
    textDecoration: "none",
    fontFamily: "inherit",
    fontSize: "inherit",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    borderColor: "transparent",
    margin: "0",
    "&:disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
    position: "absolute",
    display: "flex",
    color: theme.colors.gray["400"],
    padding: "4px",
    "&:focus": { outline: 0 },
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  heart: {
    top: (props) => (props.withMap ? "36px" : "4px"), // make room for close button on map cards
    right: "4px",
  },
  heartActive: {
    color: theme.colors.pink["500"],
  },
  video: {
    position: "absolute",
    color: theme.colors.gray["400"],
    top: (props) => (props.withMap ? "68px" : "36px"), // make room for close button on map cards
    right: "4px",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    padding: "2px 12px",
  },
  topDetails: {
    display: "flex",
    lineHeight: "1.6",
    justifyContent: "space-between",
  },
  type: {
    color: theme.colors.gray["600"],
    fontSize: "14px",
    fontWeight: "600",
    paddingTop: "6px",
    letterSpacing: ".3px",
  },
  status: {
    color: theme.colors.gray["600"],
    fontSize: "13px",
    fontWeight: "600",
    backgroundColor: theme.colors.gray["100"],
    borderRadius: "4px",
    padding: "2px 10px",
    margin: "-4px 0px 2px 6px",
    letterSpacing: ".4px",
    whiteSpace: "nowrap",
  },
  price: {
    color: theme.colors.gray["900"],
    letterSpacing: ".3px",
    fontSize: "22px",
    fontWeight: "600",
  },
  range: {
    color: theme.colors.gray["900"],
    fontSize: "20px",
    fontWeight: "600",
  },
  address: {
    color: theme.colors.gray["600"],
    fontSize: "15px",
    lineHeight: "1.3",
    fontWeight: "400",
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  compliance: {
    marginTop: theme.spacing.unit * 0.5,
  },
  moreDetails: {
    color: theme.colors.gray["700"],
    fontSize: "12px",
    lineHeight: "1.2",
    fontWeight: "400",
  },
}))(withGlobalContext(ListingCard));
